// Attaches artifact evidence to its exact tool owner without rendering an
// artifact card or payload detail.
import { z } from 'zod';

import {
    defineCompoundMember,
    type SlotKey,
} from '@apps/web/features/phoebe_chat_v3/registry/types';

const nonblankStringSchema = z
    .string()
    .refine((value) => value.trim().length > 0);
const artifactUpdatedPayloadSchema = z
    .object({
        call_id: nonblankStringSchema.optional(),
        tool_call_id: nonblankStringSchema.optional(),
        command_id: z.uuid().optional(),
    })
    .strip()
    .refine(
        (payload) =>
            payload.call_id !== undefined ||
            payload.tool_call_id !== undefined ||
            payload.command_id !== undefined,
    );

export const artifactUpdatedDefinitions = {
    artifact_updated: defineCompoundMember('artifact_updated', {
        disposition: 'compound_member',
        schema: artifactUpdatedPayloadSchema,
        malformed: { kind: 'skip' },
        evidence: null,
        slotMember: {
            slot: 'artifacts',
            terminal: false,
            keys(payload, occurrence) {
                const keys: Array<SlotKey> = [];
                // Keep this order stable: envelope causality is strongest,
                // followed by the producer's call and command aliases.
                const causedByEventId =
                    occurrence.committed?.caused_by_event_id;
                if (causedByEventId !== null && causedByEventId !== undefined) {
                    keys.push({ kind: 'event_id', value: causedByEventId });
                }
                if (payload.call_id !== undefined) {
                    keys.push({ kind: 'call_id', value: payload.call_id });
                }
                if (payload.tool_call_id !== undefined) {
                    keys.push({
                        kind: 'call_id',
                        value: payload.tool_call_id,
                    });
                }
                if (payload.command_id !== undefined) {
                    keys.push({
                        kind: 'command_id',
                        value: payload.command_id,
                    });
                }
                return keys;
            },
            fragment(_payload, occurrence) {
                return {
                    kind: 'artifact_updated',
                    sourceEventId:
                        occurrence.committed?.id ??
                        occurrence.pending?.id ??
                        occurrence.occurrenceId,
                };
            },
        },
    }),
} as const;

export const artifactUpdatedContractFixtures = {
    valid: { call_id: 'call-1' },
    malformed: { call_id: null },
} as const;
